/**
 * 饼图组件
 *
 * 使用 ECharts 实现的饼图，用于展示占比分布
 */

import { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import { PieChart as EChartsPieChart, PieSeriesOption } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { AggregatedDataItem } from '@/types/analysis';
import './PieChart.css';

echarts.use([
  EChartsPieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  CanvasRenderer,
]);

interface PieChartProps {
  data: AggregatedDataItem[];
  height?: number;
  title?: string;
  loading?: boolean;
  showLegend?: boolean;
  radius?: string | [string, string]; // 饼图半径，支持环形图
}

export function PieChart({
  data,
  height = 400,
  title,
  loading = false,
  showLegend = true,
  radius = ['40%', '70%'] // 默认环形图
}: PieChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!chartRef.current || loading) return;

    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current);
    }

    const chart = chartInstanceRef.current;

    // 检查实例是否已被销毁
    if (!chart || chart.isDisposed()) return;

    // 转换数据格式为 ECharts 饼图所需格式
    const pieData = data.map((item) => ({
      name: item.label,
      value: item.value,
    }));

    const option: echarts.EChartsCoreOption = {
      title: {
        text: title,
        left: 'center',
        top: 10,
        textStyle: {
          fontSize: 16,
          fontWeight: 600,
          color: '#1F1F1F',
        },
      },
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#E8E8E8',
        borderWidth: 1,
        textStyle: {
          color: '#5C5C5C',
        },
        formatter: (params: any) => {
          return `${params.name}<br/>数量: <strong>${params.value}</strong><br/>占比: <strong>${params.percent}%</strong>`;
        },
      },
      legend: showLegend ? {
        orient: 'vertical',
        right: '10%',
        top: 'center',
        textStyle: {
          color: '#5C5C5C',
          fontSize: 12,
        },
        formatter: (name: string) => {
          const item = data.find((d) => d.label === name);
          return `${name} (${item?.percentage?.toFixed(1) || 0}%)`;
        },
      } : undefined,
      series: [
        {
          type: 'pie',
          radius,
          center: ['40%', '50%'],
          data: pieData,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
          label: {
            show: true,
            formatter: '{b}: {d}%',
            fontSize: 12,
            color: '#5C5C5C',
          },
          labelLine: {
            show: true,
            length: 15,
            length2: 10,
          },
          itemStyle: {
            borderRadius: 5,
            borderColor: '#fff',
            borderWidth: 2,
          },
        } as PieSeriesOption,
      ],
    };

    chart.setOption(option, { notMerge: false });

    const handleResize = () => {
      if (chart && !chart.isDisposed()) {
        chart.resize();
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [data, title, loading, showLegend, radius]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="pie-chart-loading" style={{ height }}>
        <div className="loading-spinner">加载中...</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="pie-chart-empty" style={{ height }}>
        <div className="empty-message">暂无数据</div>
      </div>
    );
  }

  return <div ref={chartRef} className="pie-chart" style={{ height }} />;
}
